import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import addressApi from "api/landlord/address";
import { getErrorMessage } from "utils/error";
import {
  ApplyBrandingResponse,
  BrandingTarget,
  BrandingTargetsResponse,
  Property
} from "types/landlord/address";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  InputSize,
  Modal
} from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { ApplyBrandingResultDialog } from "./ApplyBrandingResultDialog";
import { BrandingTargetsList } from "./BrandingTargetsList";
import { BrandingToApplyPreview } from "./BrandingToApplyPreview";
import { OverwriteBrandingDialog } from "./OverwriteBrandingDialog";
import {
  BRANDING_TARGETS_PER_PAGE,
  getApplyErrorMessage,
  isPmsSynced,
  MAX_BRANDING_TARGETS,
  propertiesLabel,
  type PropertyDictionary
} from "./helpers";

const POLL_TIMEOUT_MS = 2 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 500;

const getSuccessMessage = (count: number, dictionary: PropertyDictionary) =>
  `Branding applied to ${count} ${propertiesLabel(count, dictionary)}`;

export const ApplyBrandingModal = ({
  opened,
  onClose,
  sourceProperty,
  showLandlordFields
}: {
  opened: boolean;
  onClose: () => void;
  sourceProperty: Property;
  showLandlordFields: boolean;
}) => {
  const queryClient = useQueryClient();
  const dictionary = useUnitsPropertiesDictionary();
  // Search is server-side, so a page of results never contains every selected row — keep the whole
  // target next to its id or the overwrite warning and result dialog lose them.
  const [selected, setSelected] = useState<Map<number, BrandingTarget>>(
    () => new Map()
  );
  // "explicit" = hand-picked via checkboxes, capped at MAX_BRANDING_TARGETS (selected, above).
  // "all" = every property matching the current search, no cap — excludedIds is how "select
  // all, then deselect a few" works without ever needing to enumerate the full matching set
  // client-side (which could be thousands of rows).
  const [applyMode, setApplyMode] = useState<"explicit" | "all">("explicit");
  const [excludedIds, setExcludedIds] = useState<Set<number>>(() => new Set());
  const [search, setSearch] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [bulkApplyId, setBulkApplyId] = useState<number | null>(null);
  const [result, setResult] = useState<ApplyBrandingResponse | null>(null);
  const handledBulkApplyRef = useRef<number | null>(null);

  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  // "All" mode's scope is "everything matching the search at the moment Select all was
  // clicked" — if the search then changes, silently keeping that scope live would apply to
  // a different (possibly much larger) set than what the user saw and confirmed. Drop back
  // to explicit mode instead of tracking the new search, so re-entering "all" is a deliberate
  // re-click under the new results rather than an invisible scope change.
  //
  // Adjusted during render (not a useEffect) per React's "adjusting state when a prop
  // changes" pattern — comparing against a stored previous value and calling setState
  // directly in the render body makes the reset visible in this same render instead of
  // costing an extra effect-triggered pass.
  const [previousSearch, setPreviousSearch] = useState(debouncedSearch);
  if (previousSearch !== debouncedSearch) {
    setPreviousSearch(debouncedSearch);
    if (applyMode === "all") {
      setApplyMode("explicit");
      setExcludedIds(new Set());
    }
  }

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery<BrandingTargetsResponse>({
    queryKey: [
      "properties",
      String(sourceProperty.id),
      "branding_targets",
      debouncedSearch,
      BRANDING_TARGETS_PER_PAGE
    ],
    queryFn: ({ pageParam }) =>
      addressApi.getBrandingTargets({
        propertyId: sourceProperty.id,
        page: Number(pageParam),
        per_page: BRANDING_TARGETS_PER_PAGE,
        search: debouncedSearch.trim() || undefined
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.current_page + 1 : undefined,
    enabled: opened
  });

  const targets = useMemo<BrandingTarget[]>(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  );

  const targetById = useMemo(
    () => new Map(targets.map((target) => [target.id, target])),
    [targets]
  );

  // Snapshots keep selections that the current page/search no longer contains, but prefer fresh
  // data when it is available so a refetch can't leave the warning stale. Only meaningful in
  // "explicit" mode — "all" mode's set is unbounded, so there's no itemized list to show; see the
  // generic overwrite notice near the Apply button instead.
  const overwriteTargets = useMemo(
    () =>
      Array.from(selected.values())
        .map((target) => targetById.get(target.id) ?? target)
        .filter((target) => target.has_existing_branding),
    [selected, targetById]
  );

  const totalMatchingCount = data?.pages[0]?.total_count ?? 0;
  const isAllMode = applyMode === "all";
  // What the checklist should render as checked: every loaded target not explicitly deselected
  // in "all" mode (and never a PMS-synced one — the backend excludes those outright, matching
  // that they've never been selectable by hand either), or exactly the hand-picked set in
  // "explicit" mode.
  const checkedIds = useMemo(() => {
    if (!isAllMode) return selected;

    const map = new Map<number, BrandingTarget>();
    targets.forEach((target) => {
      if (!excludedIds.has(target.id) && !isPmsSynced(target))
        map.set(target.id, target);
    });
    return map;
  }, [isAllMode, selected, targets, excludedIds]);

  const selectedCount = isAllMode
    ? Math.max(totalMatchingCount - excludedIds.size, 0)
    : selected.size;
  // The apply cap only bounds a single synchronous explicit-id request — "all" mode has no cap,
  // it's walked in batches server-side.
  const isOverCap = !isAllMode && selected.size > MAX_BRANDING_TARGETS;

  // Applying can take a while for owners with many properties, so the API enqueues the run and
  // returns immediately — we poll its status until it settles.
  const {
    data: statusData,
    isError: isStatusError,
    error: statusError
  } = useQuery({
    queryKey: [
      "properties",
      String(sourceProperty.id),
      "apply_branding",
      bulkApplyId
    ],
    queryFn: () =>
      addressApi.getApplyBrandingStatus({
        propertyId: sourceProperty.id,
        bulkApplyId: bulkApplyId as number
      }),
    enabled: bulkApplyId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "completed" || status === "failed" ? false : 1000;
    }
  });

  const isApplying =
    bulkApplyId !== null &&
    statusData?.status !== "completed" &&
    statusData?.status !== "failed";

  const { mutate: applyBranding, isPending: isEnqueuing } = useMutation({
    mutationKey: ["BULK", "APPLY", "BRANDING"],
    mutationFn: (
      payload:
        | { target_property_ids: number[] }
        | {
            apply_to_all: true;
            search?: string;
            excluded_property_ids?: number[];
          }
    ) =>
      addressApi.applyBranding({
        propertyId: sourceProperty.id,
        ...payload
      }),
    onSuccess: (response) => {
      setBulkApplyId(response.id);
    },
    onError: (mutationError) => {
      setIsConfirmOpen(false);
      toast.error(
        `Failed to apply branding: ${getApplyErrorMessage(mutationError, dictionary)}`
      );
    }
  });

  const isPending = isEnqueuing || isApplying;

  const closeAndReset = useCallback(() => {
    setSelected(new Map());
    setApplyMode("explicit");
    setExcludedIds(new Set());
    setSearch("");
    setIsConfirmOpen(false);
    setBulkApplyId(null);
    setResult(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!statusData) return;

    const settled =
      statusData.status === "completed" || statusData.status === "failed";
    if (!settled || handledBulkApplyRef.current === statusData.id) return;

    handledBulkApplyRef.current = statusData.id;
    queryClient.invalidateQueries({ queryKey: ["properties"] });

    if (
      statusData.status === "failed" ||
      statusData.failed.length > 0 ||
      statusData.skipped_fields.length > 0
    ) {
      setResult(statusData);
      setBulkApplyId(null);
      setIsConfirmOpen(false);
      return;
    }

    toast.success(getSuccessMessage(statusData.updated_count, dictionary));
    closeAndReset();
  }, [statusData, queryClient, closeAndReset, dictionary]);

  useEffect(() => {
    if (bulkApplyId === null || !isStatusError) return;

    toast.error(
      `Failed to check apply progress: ${getErrorMessage(statusError)}`
    );
    setBulkApplyId(null);
    setIsConfirmOpen(false);
  }, [bulkApplyId, isStatusError, statusError]);

  useEffect(() => {
    if (bulkApplyId === null) return;

    // A stalled job must not poll forever — it keeps running server-side, so stop and say so.
    const timer = setTimeout(() => {
      toast.error(
        "Applying branding is taking longer than expected. It will finish in the background — reload the page to see the result."
      );
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      setBulkApplyId(null);
      setIsConfirmOpen(false);
    }, POLL_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [bulkApplyId, queryClient]);

  const handleClose = () => {
    if (isEnqueuing) return;

    // The enqueued run finishes server-side, so closing mid-apply must not trap the user — just
    // refresh the affected properties on the way out.
    if (isApplying) queryClient.invalidateQueries({ queryKey: ["properties"] });

    closeAndReset();
  };

  const handleConfirmClose = () => {
    if (isEnqueuing) return;

    if (isApplying) {
      handleClose();
      return;
    }

    setIsConfirmOpen(false);
  };

  const toggleTarget = (target: BrandingTarget) => {
    if (isAllMode) {
      setExcludedIds((prev) => {
        const next = new Set(prev);
        if (next.has(target.id)) next.delete(target.id);
        else next.add(target.id);

        return next;
      });
      return;
    }

    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(target.id)) next.delete(target.id);
      else next.set(target.id, target);

      return next;
    });
  };

  // "Select all" switches to apply-to-all mode (unbounded, walked server-side) rather than
  // enumerating ids client-side — the bug this replaced only ever selected whatever page had
  // already loaded. "Deselect all" from that mode just drops back to an empty explicit selection.
  const handleSelectAllClick = () => {
    setSelected(new Map());
    setExcludedIds(new Set());
    setApplyMode(isAllMode ? "explicit" : "all");
  };

  const runApply = () => {
    if (isAllMode) {
      applyBranding({
        apply_to_all: true,
        search: debouncedSearch.trim() || undefined,
        excluded_property_ids: Array.from(excludedIds)
      });
      return;
    }

    applyBranding({ target_property_ids: Array.from(selected.keys()) });
  };

  const handleApplyClick = () => {
    if (!isAllMode && overwriteTargets.length > 0) {
      setIsConfirmOpen(true);
      return;
    }

    runApply();
  };

  return (
    <>
      <Modal
        onClose={handleClose}
        opened={opened && !isConfirmOpen && result === null}
        panelClassName="flex max-h-[90vh] w-full max-w-[720px] flex-col overflow-hidden"
      >
        <header className="shrink-0 border-b border-alpha-40 p-6">
          <div className="flex items-start justify-between gap-4">
            <h3 className="text-xl font-bold text-content-1000">
              Apply branding to multiple {dictionary.properties}
            </h3>
            <IconButton
              HugeIcon={Cancel01Icon}
              disabled={isEnqueuing}
              onClick={handleClose}
              size={IconButtonSize.LG}
              variant={IconButtonVariant.TEXT_BASE}
            />
          </div>
          <p className="mt-2 text-sm text-content-400">
            Branding saved on {sourceProperty.name} will be applied to the{" "}
            {dictionary.properties} you select below.
          </p>
        </header>

        {sourceProperty.branding ? (
          <div className="shrink-0 border-b border-alpha-40 px-6 py-4">
            <BrandingToApplyPreview
              branding={sourceProperty.branding}
              showLandlordFields={showLandlordFields}
            />
          </div>
        ) : null}

        <div className="flex min-h-0 flex-1 flex-col space-y-2 px-6 py-4">
          <p className="shrink-0 text-xs font-medium text-content-400">
            {dictionary.Properties}
          </p>

          <div className="flex shrink-0 items-center justify-between gap-4">
            <BaseInput
              className="w-full max-w-[293px]"
              id="branding-target-search"
              onChange={setSearch}
              placeholder="Search"
              size={InputSize.SM}
              type="search"
              value={search}
            />
            <Button
              className="shrink-0"
              disabled={totalMatchingCount === 0}
              onClick={handleSelectAllClick}
              variant={ButtonVariant.TEXT}
            >
              {isAllMode ? "Deselect all" : "Select all"}
            </Button>
          </div>

          {isAllMode ? (
            <p className="shrink-0 text-xs text-content-300">
              Applying to every {dictionary.property} matching your search —
              uncheck any you want to skip. This overwrites existing branding on
              affected {dictionary.properties}.
            </p>
          ) : null}

          <BrandingTargetsList
            error={error}
            hasNextPage={hasNextPage}
            hasSearch={Boolean(debouncedSearch.trim())}
            isError={isError}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={isLoading}
            onLoadMore={fetchNextPage}
            onToggle={toggleTarget}
            selectedIds={checkedIds}
            visibleTargets={targets}
          />
        </div>

        <footer className="flex shrink-0 items-center justify-between gap-4 border-t border-alpha-40 px-6 py-4">
          <div className="min-w-0 space-y-1">
            {isApplying && statusData?.total_count ? (
              <>
                <p className="text-sm text-content-400">
                  Applying to {statusData.processed_count} of{" "}
                  {statusData.total_count}{" "}
                  {propertiesLabel(statusData.total_count, dictionary)}...
                </p>
                <div className="h-1.5 w-48 overflow-hidden rounded-full bg-alpha-80">
                  <div
                    className="h-full rounded-full bg-primary-500 transition-all duration-200"
                    style={{
                      width: `${Math.min(100, Math.round((statusData.processed_count / statusData.total_count) * 100))}%`
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-content-400">
                  {selectedCount} {propertiesLabel(selectedCount, dictionary)}{" "}
                  selected
                </p>
                {isOverCap ? (
                  <p className="text-sm font-medium text-danger-500">
                    You can apply to at most {MAX_BRANDING_TARGETS}{" "}
                    {dictionary.properties} at a time.
                  </p>
                ) : null}
              </>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button disabled={isEnqueuing} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              disabled={selectedCount === 0 || isOverCap}
              loading={isPending}
              onClick={handleApplyClick}
              variant={ButtonVariant.PRIMARY}
            >
              Apply branding
            </Button>
          </div>
        </footer>
      </Modal>

      <OverwriteBrandingDialog
        affectedTargets={overwriteTargets}
        canClose={!isEnqueuing}
        isPending={isPending}
        onClose={handleConfirmClose}
        onConfirm={runApply}
        opened={isConfirmOpen}
        selectedCount={selected.size}
        sourceName={sourceProperty.name}
      />

      <ApplyBrandingResultDialog
        onClose={closeAndReset}
        result={result}
        targets={isAllMode ? targets : Array.from(selected.values())}
      />
    </>
  );
};
